import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { Box, Drawer, IconButton, Tooltip, Badge } from '@mui/material';
import { ChevronLeft, ChevronRight, Send, Add } from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useSelector, useDispatch } from 'react-redux';
import { setNavbarCollapsed } from 'src/store/slices/navbarSlice';

import Logo from 'src/components/Logo';
import NavBarButton from 'src/components/NavBar/NavBarButton';
import ProfileDropdown from 'src/components/ProfileDropdown';
import Help from './Help';
import { useRouteAccess } from 'src/hooks/useRouteAccess';
import QueryFormModal from 'src/components/QueryFormModal';

const DRAWER_WIDTH = 240;
const COLLAPSED_WIDTH = 72;

function NavBar() {
  const unreadCount = useSelector((state) => state.unread);
  const location = useLocation();
  const [activeEl, setActiveEl] = useState(null);
  const [createQueryModalOpen, setCreateQueryModalOpen] = useState(false);
  const isCollapsed = useSelector((state) => state.navbar?.isCollapsed) || false;
  const activeStatus = useSelector((state) => state.navbar?.activeStatus);
  const dispatch = useDispatch();
  const ref = useRef(null);
  const { filteredRoutes } = useRouteAccess();

  const isActive = useMemo(
    () => (path, status) => {
      if (activeStatus) {
        return Array.isArray(status) ? status.includes(activeStatus) : status === activeStatus;
      }
      return location.pathname.includes(path);
    },
    [activeStatus, location.pathname]
  );

  const renderIcon = useMemo(() => {
    const IconComponent = (Icon) => {
      if (Icon === Send && unreadCount > 0) {
        return (
          <Badge badgeContent={unreadCount} color="primary">
            <Icon />
          </Badge>
        );
      }
      return <Icon />;
    };
    IconComponent.displayName = 'NavBarIcon';
    return IconComponent;
  }, [unreadCount]);

  const toggleDrawer = useCallback(() => {
    dispatch(setNavbarCollapsed(!isCollapsed));
  }, [dispatch, isCollapsed]);

  useEffect(() => {
    const activeButton = ref.current?.querySelector('button.active');
    setActiveEl(activeButton);
  }, [location.pathname, activeStatus]);

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: isCollapsed ? COLLAPSED_WIDTH : DRAWER_WIDTH,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: isCollapsed ? COLLAPSED_WIDTH : DRAWER_WIDTH,
          boxSizing: 'border-box',
          borderRight: '1px solid rgba(0, 0, 0, 0.12)',
          transition: (theme) =>
            theme.transitions.create(['width'], {
              duration: theme.transitions.duration.standard,
            }),
          overflowX: 'hidden',
        },
      }}
    >
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box
          sx={{
            py: 3,
            pl: 3,
            pr: isCollapsed ? 3 : 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: isCollapsed ? 'center' : 'space-between',
          }}
        >
          {!isCollapsed && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Logo />
            </Box>
          )}
          <IconButton onClick={toggleDrawer}>{isCollapsed ? <ChevronRight /> : <ChevronLeft />}</IconButton>
        </Box>

        <Box
          ref={ref}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
            flex: 1,
            px: 1,
          }}
        >
          <NavBarButton
            onClick={() => setCreateQueryModalOpen(true)}
            Icon={() => renderIcon(Add)}
            label="New Query"
            isActive={false}
            vertical
            singleLine
            variant="filled"
            sx={{
              my: 2,
            }}
          />

          {filteredRoutes.topRoutes.map(({ path, label, Icon, status }) => (
            <Tooltip title={label} placement="right" key={path}>
              <Box>
                <NavBarButton path={path} Icon={() => renderIcon(Icon)} label={label} isActive={isActive(path, status)} vertical singleLine />
              </Box>
            </Tooltip>
          ))}
          <Box sx={{ mt: 'auto' }}>
            {filteredRoutes.bottomRoutes.map(({ path, label, Icon, status }) => (
              <Tooltip title={label} placement="right" key={path}>
                <Box>
                  <NavBarButton path={path} Icon={() => renderIcon(Icon)} label={label} isActive={isActive(path, status)} vertical singleLine />
                </Box>
              </Tooltip>
            ))}
          </Box>
          {activeEl && (
            <motion.div
              initial={false}
              animate={{
                y: activeEl.offsetTop + 8,
                height: activeEl.offsetHeight - 16,
              }}
              transition={{ ease: 'easeOut', duration: 0.3 }}
              style={{
                width: '4px',
                backgroundColor: '#47A2F7',
                position: 'absolute',
                left: '8px',
                borderRadius: '0 4px 4px 0',
              }}
            />
          )}
        </Box>

        <Box sx={{ mx: 1, mb: 3, mt: 'auto' }}>
          <Help />
          <ProfileDropdown collapsed={isCollapsed} />
        </Box>
      </Box>

      <QueryFormModal
        open={createQueryModalOpen}
        onClose={() => setCreateQueryModalOpen(false)}
        onSuccess={() => {
          setCreateQueryModalOpen(false);
          // window.location.reload();
        }}
      />
    </Drawer>
  );
}

export default NavBar;
